// API base URL — build-time env ONLY, no fallback. An unset value used to fall
// back to a hardcoded localhost:8080, which silently pointed a misconfigured
// production build at nothing and hid the missing variable. Now the build
// carries exactly what the environment said; a missing value is reported here
// and every request then fails against the page origin, which is loud.
const API_URL = process.env.REACT_APP_API_URL || "";
if (!API_URL) {
  console.error(
    "[config] REACT_APP_API_URL is not set. Set it in the build environment " +
      "(App Platform build-time env for production, .env.local for development)."
  );
}

// ─── Same-site guard for the session cookie ──────────────────────────────────
// The admin session lives ONLY in the httpOnly `bg_admin_token` cookie — the JWT
// is deliberately never persisted (see helpers/jwt-token-access/sessionToken.js).
// That design works if and only if the API is on the SAME SITE as the dashboard.
//
// If the API sits on a different registrable domain, the cookie is a *third-party*
// cookie. `SameSite=None; Secure` is necessary for such a cookie to be sent, but it
// is never sufficient: Safari (ITP) and Brave block third-party cookies outright,
// and Chrome does too in Incognito or with third-party cookies disabled. The cookie
// is then silently discarded, so every page reload becomes a logout and
// "Remember me for 90 days" can never do anything — the 90 days is carried entirely
// by that cookie.
//
// This failure is completely invisible at runtime: no error, no warning, just a
// login screen. It has bitten this dashboard repeatedly because the API base is
// injected at build time and cannot be reviewed from the repo. So assert it here
// and fail loudly instead of silently signing everyone out.
//
// Heuristic: compare the last two labels of each hostname. Deliberately NOT a full
// Public Suffix List check — the common real-world mistake is pointing at a PaaS
// host (e.g. *.ondigitalocean.app, *.vercel.app), and those differ from the
// dashboard's own domain in the last two labels anyway, so this catches them.
function registrableish(hostname) {
  const labels = String(hostname || "").split(".");
  return labels.length <= 2 ? labels.join(".") : labels.slice(-2).join(".");
}

if (typeof window !== "undefined" && window.location && window.location.hostname) {
  try {
    const pageSite = registrableish(window.location.hostname);
    const apiSite = registrableish(new URL(API_URL, window.location.origin).hostname);
    if (pageSite !== apiSite) {
      console.error(
        `[auth] API base "${API_URL}" is cross-site with ${window.location.origin} ` +
          `(${apiSite} ≠ ${pageSite}). The bg_admin_token session cookie will be treated ` +
          `as a third-party cookie and dropped by Safari/Brave/Chrome-incognito, so sessions ` +
          `will not survive a page reload and "Remember me" will not work. ` +
          `Fix REACT_APP_API_URL to point at an API host on ${pageSite}.`
      );
    }
  } catch (_) {
    /* malformed URL — the request layer will surface it */
  }
}

const WANK_CHAIN_ID = Number(process.env.REACT_APP_WANK_CHAIN_ID || process.env.REACT_APP_BYB_CHAIN_ID || 4663);

module.exports = {
  API_URL,
  // $WANK payout settings (all public, non-secret: used to build the ERC-20
  // transfer the admin signs in their wallet). Override per environment. The
  // REACT_APP_WANK_* vars win; the old REACT_APP_BYB_* names still work as a
  // fallback so an existing deploy keeps paying out until its env is renamed.
  // Claims pay out on Robinhood Chain (4663).
  rewards: {
    TOKEN_ADDRESS: process.env.REACT_APP_WANK_TOKEN_ADDRESS || process.env.REACT_APP_BYB_TOKEN_ADDRESS || "",
    TOKEN_DECIMALS: Number(process.env.REACT_APP_WANK_TOKEN_DECIMALS || process.env.REACT_APP_BYB_TOKEN_DECIMALS || 18),
    CHAIN_ID: WANK_CHAIN_ID,
    CHAIN_NAME:
      process.env.REACT_APP_WANK_CHAIN_NAME ||
      process.env.REACT_APP_BYB_CHAIN_NAME ||
      (WANK_CHAIN_ID === 4663 ? "Robinhood Chain" : `chain ${WANK_CHAIN_ID}`),
    // Optional: lets the wallet add the chain if it does not know it yet.
    CHAIN_RPC_URL: process.env.REACT_APP_WANK_RPC_URL || "",
    CHAIN_EXPLORER_URL: process.env.REACT_APP_WANK_EXPLORER_URL || "",
  },
}
